import React, { createContext, useContext, useState, useMemo } from 'react';

type ColorMode = 'light' | 'dark';
type FontScale = 'normal' | 'compact';

interface UiSettingsContextValue {
  mode: ColorMode;
  fontScale: FontScale;
  hintsEnabled: boolean;
  hintsResetKey: number;
  toggleColorMode: () => void;
  toggleFontScale: () => void;
  toggleHints: () => void;
  resetHints: () => void;
}

const UiSettingsContext = createContext<UiSettingsContextValue>({
  mode: 'light',
  fontScale: 'normal',
  hintsEnabled: true,
  hintsResetKey: 0,
  toggleColorMode: () => {},
  toggleFontScale: () => {},
  toggleHints: () => {},
  resetHints: () => {},
});

export const useUiSettings = () => useContext(UiSettingsContext);

export const UiSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ColorMode>(() =>
    (localStorage.getItem('color-mode') as ColorMode) || 'light',
  );

  const [fontScale, setFontScale] = useState<FontScale>(() =>
    (localStorage.getItem('font-scale') as FontScale) || 'normal',
  );

  const [hintsEnabled, setHintsEnabled] = useState<boolean>(() =>
    localStorage.getItem('ui-hints-enabled') !== 'false',
  );

  const [hintsResetKey, setHintsResetKey] = useState(0);

  const value = useMemo(
    () => ({
      mode,
      fontScale,
      hintsEnabled,
      hintsResetKey,
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('color-mode', next);
          return next;
        });
      },
      toggleFontScale: () => {
        setFontScale((prev) => {
          const next = prev === 'normal' ? 'compact' : 'normal';
          localStorage.setItem('font-scale', next);
          return next;
        });
      },
      toggleHints: () => {
        setHintsEnabled((prev) => {
          const next = !prev;
          localStorage.setItem('ui-hints-enabled', String(next));
          return next;
        });
      },
      resetHints: () => {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('page-hint-dismissed:')) {
            localStorage.removeItem(key);
          }
        });
        setHintsResetKey((prev) => prev + 1);
      },
    }),
    [mode, fontScale, hintsEnabled, hintsResetKey],
  );

  return <UiSettingsContext.Provider value={value}>{children}</UiSettingsContext.Provider>;
};
